import { clamp } from "./utils.js";

export const HAND_LANDMARK_COUNT = 21;

const PALM_INDICES = [0, 5, 9, 13, 17];
const FINGER_TIP_INDICES = {
  thumb: 4,
  index: 8,
  middle: 12,
  ring: 16,
  pinky: 20,
};

/**
 * MediaPipe HandLandmarker lifecycle and data-normalization boundary.
 *
 * This module intentionally does not control Three.js. It emits stable hand
 * frames that the stage-06 interaction controller can interpret later.
 */
export class GestureController {
  constructor({
    modelPath = "/models/hand_landmarker.task",
    wasmPath = "/mediapipe/wasm",
    maxInferenceFps = 24,
    mirror = true,
    minDetectionConfidence = 0.55,
    minPresenceConfidence = 0.55,
    minTrackingConfidence = 0.55,
  } = {}) {
    this.modelPath = modelPath;
    this.wasmPath = wasmPath;
    this.maxInferenceFps = clamp(maxInferenceFps, 5, 60);
    this.mirror = mirror;
    this.minDetectionConfidence = minDetectionConfidence;
    this.minPresenceConfidence = minPresenceConfidence;
    this.minTrackingConfidence = minTrackingConfidence;

    this.handLandmarker = null;
    this.delegate = null;
    this.initializationPromise = null;
    this.startPromise = null;
    this.stream = null;
    this.video = null;
    this.running = false;
    this.debug = false;
    this.videoFrameHandle = 0;
    this.animationFrameHandle = 0;
    this.lastVideoTime = -1;
    this.lastInferenceTime = -Infinity;
    this.consecutiveErrors = 0;
    this.listeners = new Set();
    this.snapshot = createEmptyHandFrame("idle");

    this.handleVideoFrame = this.handleVideoFrame.bind(this);
    this.handleAnimationFrame = this.handleAnimationFrame.bind(this);
  }

  async initialize() {
    if (this.handLandmarker) {
      return this.handLandmarker;
    }
    if (this.initializationPromise) {
      return this.initializationPromise;
    }

    this.initializationPromise = this.createLandmarker();

    try {
      this.handLandmarker = await this.initializationPromise;
      return this.handLandmarker;
    } finally {
      this.initializationPromise = null;
    }
  }

  async createLandmarker() {
    const { FilesetResolver, HandLandmarker } = await import(
      "@mediapipe/tasks-vision"
    );
    const wasmRoot = resolveAssetPath(this.wasmPath);
    const modelAssetPath = resolveAssetPath(this.modelPath);
    const vision = await FilesetResolver.forVisionTasks(wasmRoot);
    const commonOptions = {
      runningMode: "VIDEO",
      numHands: 1,
      minHandDetectionConfidence: this.minDetectionConfidence,
      minHandPresenceConfidence: this.minPresenceConfidence,
      minTrackingConfidence: this.minTrackingConfidence,
    };

    try {
      const landmarker = await HandLandmarker.createFromOptions(vision, {
        ...commonOptions,
        baseOptions: {
          modelAssetPath,
          delegate: "GPU",
        },
      });
      this.delegate = "GPU";
      return landmarker;
    } catch {
      try {
        const landmarker = await HandLandmarker.createFromOptions(vision, {
          ...commonOptions,
          baseOptions: {
            modelAssetPath,
            delegate: "CPU",
          },
        });
        this.delegate = "CPU";
        return landmarker;
      } catch (error) {
        throw new GestureControllerError(
          "model-load-failed",
          "MediaPipe hand model could not be initialized.",
          error,
        );
      }
    }
  }

  async start(videoElement) {
    if (this.running) {
      return this.snapshot;
    }
    if (this.startPromise) {
      return this.startPromise;
    }

    this.startPromise = this.startInternal(videoElement);
    try {
      return await this.startPromise;
    } finally {
      this.startPromise = null;
    }
  }

  async startInternal(videoElement) {
    if (!(videoElement instanceof HTMLVideoElement)) {
      throw new GestureControllerError(
        "invalid-video",
        "A video element is required for hand tracking.",
      );
    }
    if (!globalThis.isSecureContext) {
      throw new GestureControllerError(
        "insecure-context",
        "Camera access requires HTTPS or localhost.",
      );
    }
    if (!navigator.mediaDevices?.getUserMedia) {
      throw new GestureControllerError(
        "camera-unavailable",
        "Camera capture is not supported by this browser.",
      );
    }

    this.stop({ publish: false });
    this.video = videoElement;
    this.publish(createEmptyHandFrame("requesting"));

    try {
      this.stream = await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: {
          facingMode: "user",
          width: { ideal: 640 },
          height: { ideal: 480 },
          frameRate: { ideal: 30, max: 60 },
        },
      });

      this.video.srcObject = this.stream;
      this.video.muted = true;
      this.video.playsInline = true;
      await waitForVideo(this.video);
      await this.initialize();

      this.running = true;
      this.lastVideoTime = -1;
      this.lastInferenceTime = -Infinity;
      this.consecutiveErrors = 0;
      this.publish(createEmptyHandFrame("searching"));
      this.scheduleNextFrame();
      return this.snapshot;
    } catch (error) {
      this.stop({ publish: false });
      const normalizedError = normalizeGestureError(error);
      this.publish(createEmptyHandFrame("error", {
        error: normalizedError.code,
      }));
      throw normalizedError;
    }
  }

  scheduleNextFrame() {
    if (!this.running || !this.video) {
      return;
    }

    if (typeof this.video.requestVideoFrameCallback === "function") {
      this.videoFrameHandle = this.video.requestVideoFrameCallback(
        this.handleVideoFrame,
      );
    } else {
      this.animationFrameHandle = requestAnimationFrame(
        this.handleAnimationFrame,
      );
    }
  }

  handleVideoFrame(now) {
    this.processVideoFrame(now);
    this.scheduleNextFrame();
  }

  handleAnimationFrame(now) {
    this.processVideoFrame(now);
    this.scheduleNextFrame();
  }

  processVideoFrame(now) {
    if (
      !this.running ||
      !this.video ||
      !this.handLandmarker ||
      document.hidden ||
      this.video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA
    ) {
      return;
    }

    const inferenceInterval = 1000 / this.maxInferenceFps;
    if (now - this.lastInferenceTime < inferenceInterval) {
      return;
    }
    if (this.video.currentTime === this.lastVideoTime) {
      return;
    }

    this.lastVideoTime = this.video.currentTime;
    this.lastInferenceTime = now;
    const inferenceStarted = performance.now();

    try {
      const result = this.handLandmarker.detectForVideo(this.video, now);
      const inferenceMs = performance.now() - inferenceStarted;
      this.consecutiveErrors = 0;
      this.publish(
        createHandFrame(
          result,
          now,
          this.snapshot,
          inferenceMs,
          this.mirror,
        ),
      );
    } catch {
      this.consecutiveErrors += 1;
      if (this.consecutiveErrors >= 3) {
        this.publish(createEmptyHandFrame("error", {
          error: "inference-failed",
          timestamp: now,
        }));
      }
    }
  }

  stop({ publish = true } = {}) {
    this.running = false;

    if (
      this.video &&
      this.videoFrameHandle &&
      typeof this.video.cancelVideoFrameCallback === "function"
    ) {
      this.video.cancelVideoFrameCallback(this.videoFrameHandle);
    }
    if (this.animationFrameHandle) {
      cancelAnimationFrame(this.animationFrameHandle);
    }

    this.videoFrameHandle = 0;
    this.animationFrameHandle = 0;
    this.stream?.getTracks().forEach((track) => track.stop());
    this.stream = null;

    if (this.video) {
      this.video.pause();
      this.video.srcObject = null;
    }
    this.video = null;
    this.lastVideoTime = -1;
    this.lastInferenceTime = -Infinity;

    if (publish) {
      this.publish(createEmptyHandFrame("stopped"));
    }
  }

  subscribe(listener) {
    this.listeners.add(listener);
    listener(this.snapshot);
    return () => this.listeners.delete(listener);
  }

  publish(frame) {
    this.snapshot = {
      ...frame,
      debug: this.debug,
      delegate: this.delegate,
    };
    this.listeners.forEach((listener) => listener(this.snapshot));
  }

  getSnapshot() {
    return this.snapshot;
  }

  setDebug(enabled) {
    this.debug = Boolean(enabled);
    this.publish(this.snapshot);
  }

  dispose() {
    this.stop({ publish: false });
    this.handLandmarker?.close();
    this.handLandmarker = null;
    this.listeners.clear();
  }
}

/**
 * Converts a MediaPipe result into the stable, renderer-independent hand frame.
 */
export function createHandFrame(
  result,
  timestamp,
  previous = null,
  inferenceMs = 0,
  mirror = true,
) {
  const sourceLandmarks = result?.landmarks?.[0];
  if (!sourceLandmarks || sourceLandmarks.length !== HAND_LANDMARK_COUNT) {
    return createEmptyHandFrame("searching", { timestamp, inferenceMs });
  }

  const landmarks = sourceLandmarks.map((point) => ({
    x: mirror ? 1 - point.x : point.x,
    y: point.y,
    z: point.z,
  }));
  const sourceWorld = result?.worldLandmarks?.[0] ?? [];
  const worldLandmarks = sourceWorld.map((point) => ({
    x: mirror ? -point.x : point.x,
    y: point.y,
    z: point.z,
  }));
  const handednessCategory = result?.handedness?.[0]?.[0] ?? null;
  const rawPalm = averagePoints(landmarks, PALM_INDICES);
  const rawSize =
    distance2D(landmarks[5], landmarks[17]) * 0.55 +
    distance2D(landmarks[0], landmarks[9]) * 0.45;
  const rawDepth = clamp((rawSize - 0.075) / 0.255, 0, 1);

  let palmX = rawPalm.x;
  let palmY = rawPalm.y;
  let palmZ = rawPalm.z;
  let palmSize = rawSize;
  let depth = rawDepth;
  let velocityX = 0;
  let velocityY = 0;

  if (previous?.detected && previous.timestamp < timestamp) {
    const delta = clamp((timestamp - previous.timestamp) / 1000, 1 / 120, 0.2);
    const smoothing = 1 - Math.exp(-13 * delta);
    palmX = mix(previous.palm.x, rawPalm.x, smoothing);
    palmY = mix(previous.palm.y, rawPalm.y, smoothing);
    palmZ = mix(previous.palm.z, rawPalm.z, smoothing);
    palmSize = mix(previous.palm.size, rawSize, smoothing);
    depth = mix(previous.palm.depth, rawDepth, smoothing);
    const measuredVelocityX = (palmX - previous.palm.x) / delta;
    const measuredVelocityY = (palmY - previous.palm.y) / delta;
    velocityX = mix(previous.palm.velocityX, measuredVelocityX, 0.58);
    velocityY = mix(previous.palm.velocityY, measuredVelocityY, 0.58);
  }

  const speed = Math.hypot(velocityX, velocityY);
  const directionX = speed > 0.012 ? velocityX / speed : 0;
  const directionY = speed > 0.012 ? velocityY / speed : 0;
  const palm = {
    x: palmX,
    y: palmY,
    z: palmZ,
    size: palmSize,
    depth,
    velocityX,
    velocityY,
    speed,
    directionX,
    directionY,
    world: worldLandmarks.length === HAND_LANDMARK_COUNT
      ? averagePoints(worldLandmarks, PALM_INDICES)
      : null,
  };

  const fingers = Object.fromEntries(
    Object.entries(FINGER_TIP_INDICES).map(([name, index]) => [
      name,
      { ...landmarks[index] },
    ]),
  );
  const fingertipValues = Object.values(fingers);
  const meanPalmDistance =
    fingertipValues.reduce(
      (total, point) => total + distance2D(point, palm),
      0,
    ) /
    fingertipValues.length /
    Math.max(palmSize, Number.EPSILON);

  return {
    detected: true,
    status: "tracking",
    confidence: handednessCategory?.score ?? 0,
    handedness: handednessCategory?.categoryName ?? null,
    landmarks,
    worldLandmarks,
    palm,
    fingers,
    fingerDistances: {
      thumbIndex:
        distance2D(fingers.thumb, fingers.index) /
        Math.max(palmSize, Number.EPSILON),
      indexMiddle:
        distance2D(fingers.index, fingers.middle) /
        Math.max(palmSize, Number.EPSILON),
      middleRing:
        distance2D(fingers.middle, fingers.ring) /
        Math.max(palmSize, Number.EPSILON),
      ringPinky:
        distance2D(fingers.ring, fingers.pinky) /
        Math.max(palmSize, Number.EPSILON),
      meanFromPalm: meanPalmDistance,
    },
    timestamp,
    inferenceMs,
    error: null,
  };
}

export function createEmptyHandFrame(
  status = "idle",
  { timestamp = 0, inferenceMs = 0, error = null } = {},
) {
  return {
    detected: false,
    status,
    confidence: 0,
    handedness: null,
    landmarks: [],
    worldLandmarks: [],
    palm: {
      x: 0.5,
      y: 0.5,
      z: 0,
      size: 0,
      depth: 0,
      velocityX: 0,
      velocityY: 0,
      speed: 0,
      directionX: 0,
      directionY: 0,
      world: null,
    },
    fingers: {},
    fingerDistances: {
      thumbIndex: 0,
      indexMiddle: 0,
      middleRing: 0,
      ringPinky: 0,
      meanFromPalm: 0,
    },
    timestamp,
    inferenceMs,
    error,
  };
}

export class GestureControllerError extends Error {
  constructor(code, message, cause) {
    super(message, { cause });
    this.name = "GestureControllerError";
    this.code = code;
  }
}

function normalizeGestureError(error) {
  if (error instanceof GestureControllerError) {
    return error;
  }

  const codeByName = {
    NotAllowedError: "permission-denied",
    NotFoundError: "camera-unavailable",
    NotReadableError: "camera-busy",
    OverconstrainedError: "camera-constraints",
    SecurityError: "permission-denied",
  };
  const code = codeByName[error?.name] ?? "camera-start-failed";
  return new GestureControllerError(
    code,
    error?.message ?? "Camera could not be started.",
    error,
  );
}

function resolveAssetPath(path) {
  return new URL(path, globalThis.location.origin).toString().replace(/\/$/, "");
}

function waitForVideo(video) {
  if (video.readyState >= HTMLMediaElement.HAVE_METADATA) {
    return video.play();
  }

  return new Promise((resolve, reject) => {
    const handleLoaded = async () => {
      cleanup();
      try {
        await video.play();
        resolve();
      } catch (error) {
        reject(error);
      }
    };
    const handleError = () => {
      cleanup();
      reject(
        new GestureControllerError(
          "camera-start-failed",
          "Camera video stream could not be played.",
        ),
      );
    };
    const cleanup = () => {
      video.removeEventListener("loadedmetadata", handleLoaded);
      video.removeEventListener("error", handleError);
    };

    video.addEventListener("loadedmetadata", handleLoaded, { once: true });
    video.addEventListener("error", handleError, { once: true });
  });
}

function averagePoints(points, indices) {
  const total = indices.reduce(
    (sum, index) => {
      sum.x += points[index].x;
      sum.y += points[index].y;
      sum.z += points[index].z;
      return sum;
    },
    { x: 0, y: 0, z: 0 },
  );

  return {
    x: total.x / indices.length,
    y: total.y / indices.length,
    z: total.z / indices.length,
  };
}

function distance2D(first, second) {
  return Math.hypot(first.x - second.x, first.y - second.y);
}

function mix(from, to, amount) {
  return from + (to - from) * amount;
}
